import { createSlice } from '@reduxjs/toolkit';

/**
 * The store: what is on the shelf, and everything that moved it.
 *
 * Two lists rather than one, because they are invalidated by different things.
 * The items list goes stale whenever a movement is posted — every posted
 * document changes a quantity and usually an average cost — but the movement
 * feed also depends on the date range the screen is showing, which the items
 * list could not care less about.
 *
 * `stockValue` is the server's figure, not a sum of this list: it is computed
 * with `Math.max(0, quantity)` because historic data can be negative, and a
 * client that re-adds it up naively gets a different number from the phone.
 *
 * Wiped on logout by the reset in store.js.
 */
const initialState = {
  stocks: [],
  stockValue: 0,
  isStockDataFetched: false,

  movements: [],
  movementSummary: null,
  isMovementDataFetched: false,

  // Where issued stock is posted. Read once — a shop adds a station about
  // once a year.
  stations: [],
  isStationDataFetched: false,
};

export const stockSlice = createSlice({
  name: 'stock',
  initialState,
  reducers: {
    setStocks: (state, action) => {
      state.stocks = action.payload?.stocks || [];
      state.stockValue = Number(action.payload?.stockValue || 0);
    },

    setStockDataFetched: (state, action) => {
      state.isStockDataFetched = action.payload;
    },

    setMovements: (state, action) => {
      state.movements = action.payload?.movements || [];
      state.movementSummary = action.payload?.summary || null;
    },

    setMovementDataFetched: (state, action) => {
      state.isMovementDataFetched = action.payload;
    },

    setStations: (state, action) => {
      state.stations = action.payload || [];
    },

    setStationDataFetched: (state, action) => {
      state.isStationDataFetched = action.payload;
    },

    /**
     * A movement was posted, edited or cancelled.
     *
     * Both lists are marked stale rather than patched. Patching would mean
     * re-deriving every average cost this document touched, which is exactly
     * the arithmetic the server does inside a transaction — and a client that
     * guesses at it shows figures that disagree with the phone.
     */
    stockChanged: (state) => {
      state.isStockDataFetched = false;
      state.isMovementDataFetched = false;
    },
  },
});

export const {
  setStocks,
  setStockDataFetched,
  setMovements,
  setMovementDataFetched,
  setStations,
  setStationDataFetched,
  stockChanged,
} = stockSlice.actions;

export default stockSlice.reducer;
